/**
 * ScreencastViewer Component
 *
 * Displays a live browser view using CDP Page.startScreencast frames
 * streamed via Server-Sent Events (SSE). Provides browser display
 * with a small attack surface (viewport-only JPEG frames, no desktop access).
 *
 * The connection itself lives in `screencast-stream`, shared by URL: several
 * components show the same session at once (the tool card, the chat widget,
 * the expanded layover) and one SSE per session is the budget.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn, Flex, Text } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/shared/auth';
import { isE2ETestMode } from '@/shared/auth/utils/e2e';

import {
  subscribeToScreencast,
  type ICachedFrame,
  type TScreencastState,
} from './screencast-stream';

export {
  frameCache,
  fatalUrlCache,
  resetScreencastStreams,
  countScreencastStreams,
  countScreencastSubscribers,
  STREAM_LINGER_MS,
} from './screencast-stream';

export type ScreencastState = TScreencastState;

/**
 * Props for the ScreencastViewer component.
 */
interface IScreencastViewerProps {
  /** The screencast SSE URL (e.g. https://app.example.com/screencast/browser-pod-name) */
  screencastUrl: string;
  /** When true, the viewer is in read-only mode (visual indicator only, no interaction endpoints). */
  readOnly?: boolean;
  /** Callback fired when the connection state changes */
  onStateChange?: (state: ScreencastState) => void;
  /** Drop the 200px minimum height, to fit a small frame. */
  fit?: boolean;
}

/**
 * ScreencastViewer renders a live browser view using CDP screencast frames.
 *
 * Frames are received as base64-encoded JPEG images via SSE and rendered
 * onto an HTML canvas for efficient display. The canvas automatically scales
 * to fit its container while maintaining the aspect ratio.
 *
 * When disconnected or ended, the last frame remains visible on the canvas
 * with a subtle overlay indicating the state.
 *
 * @param props - Component props
 * @returns ScreencastViewer component
 */
export function ScreencastViewer({
  screencastUrl,
  readOnly: _readOnly = false,
  onStateChange,
  fit = false,
}: IScreencastViewerProps) {
  'use no memo';
  const { t } = useTranslation('browser-automation');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { getAccessTokenSilently } = useAuth();
  // Store in ref so the subscribe effect doesn't re-run on token refresh.
  // The token is only needed at connection time, and the stream reads the
  // latest reference from the ref at that point.
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  getAccessTokenSilentlyRef.current = getAccessTokenSilently;
  const [state, setState] = useState<ScreencastState>('connecting');
  const fpsRef = useRef(0);
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  const hasReceivedFrameRef = useRef(false);
  const [hasReceivedFrame, setHasReceivedFrame] = useState(false);

  // Track FPS
  const frameTimesRef = useRef<number[]>([]);

  const updateFps = useCallback(() => {
    const now = performance.now();
    const frameTimes = frameTimesRef.current;
    frameTimes.push(now);

    // Keep only frames from the last second
    const cutoff = now - 1000;
    while (frameTimes.length > 0 && frameTimes[0] < cutoff) {
      frameTimes.shift();
    }

    fpsRef.current = frameTimes.length;
  }, []);

  // Render a base64 JPEG frame onto the canvas
  const renderFrame = useCallback(
    (frame: ICachedFrame) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const img = new Image();
      img.onload = () => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Update canvas internal dimensions to match the image
        if (canvas.width !== img.width || canvas.height !== img.height) {
          canvas.width = img.width;
          canvas.height = img.height;
        }

        ctx.drawImage(img, 0, 0);
        if (!hasReceivedFrameRef.current) {
          hasReceivedFrameRef.current = true;
          setHasReceivedFrame(true);
        }
        updateFps();
      };
      img.src = `data:image/jpeg;base64,${frame.image}`;
    },
    [updateFps],
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: SSE subscription lifecycle
  useEffect(() => {
    if (!screencastUrl) return;
    const headers: Record<string, string> = isE2ETestMode()
      ? { 'X-E2E-Test-Mode': 'true' }
      : {};
    return subscribeToScreencast(
      screencastUrl,
      () => getAccessTokenSilentlyRef.current(),
      headers,
      {
        onFrame: renderFrame,
        onState: next => {
          setState(next);
          onStateChangeRef.current?.(next);
        },
      },
    );
  }, [screencastUrl, renderFrame]);

  const isConnected = state === 'connected';
  const isWaiting = state === 'waiting_for_page';
  const isTerminal = state === 'ended' || state === 'disconnected';
  // Show canvas whenever we have frames — keep last screenshot visible during errors/retries
  const showCanvas = isConnected || hasReceivedFrame;

  return (
    <Flex direction="column" className="w-full h-full bg-background relative">
      {/* Canvas container - always rendered, visible when connected or showing last frame */}
      <div
        ref={containerRef}
        className={cn(
          'w-full h-full flex items-center justify-center',
          !fit && 'min-h-[200px]',
          // Not 'block': tailwind-merge would drop 'flex' and top-align the frame.
          !showCanvas && 'hidden',
        )}
      >
        <canvas
          ref={canvasRef}
          className="max-w-full max-h-full object-contain"
          style={{ imageRendering: 'auto' }}
        />
      </div>

      {/* Disconnected/Ended overlay — shown over the last frame */}
      {isTerminal && hasReceivedFrame && (
        <div className="absolute bottom-2 left-2 bg-foreground/60 text-background text-xs px-2 py-1 rounded">
          {state === 'ended'
            ? t('browserAutomation.screencast.ended')
            : t('browserAutomation.screencast.disconnected')}
        </div>
      )}

      {/* Loading / Error overlay — only shown when we have no frame to display */}
      {!showCanvas && (
        <Flex
          align="center"
          justify="center"
          className="absolute inset-0 bg-background"
        >
          <Text color="muted">
            {state === 'connecting' &&
              t('browserAutomation.screencast.connecting')}
            {isWaiting && t('browserAutomation.screencast.waitingForPage')}
            {state === 'disconnected' &&
              t('browserAutomation.screencast.disconnectedView')}
            {state === 'error' && t('browserAutomation.screencast.retrying')}
            {state === 'ended' && t('browserAutomation.screencast.endedView')}
          </Text>
        </Flex>
      )}
    </Flex>
  );
}
